// ==========================================================================
// StarPlayer App
// ==========================================================================

import support from '../support';
import browser from '../utils/browser';
import { createElement, getAttributesFromSelector, getElement, toggleClass } from '../utils/elements';
import i18n from '../utils/i18n';
import is from '../utils/is';
import loadImage from '../utils/load-image';
import { setAspectRatio } from '../utils/style';

export const ActionType = {
  STREAM: 0,
  DOWNLOAD: 1,
};
Object.freeze(ActionType);

const starplayerApp = {
  makeUrl(contentUrl = '', contentId = '', type = ActionType.STREAM) {
    const player = this;
    const {
      license,
      actionUrl,
      referer,
      debug,
      minimumVersionForAndroid,
      minimumVersionForIos,
      pmp,
      refererReturn,
      offlineCheck,
    } = player.config.starplayer.app;
    const version = browser.isAndroid ? minimumVersionForAndroid : minimumVersionForIos;
    const { userId } = player.config.starplayer;
    const { title } = player.config;
    const spkid = player.config.starplayer.spkId;
    const newActionUrl = `${actionUrl}?license=${license}&spkid=${spkid}&contenturl=${contentUrl}&contentid=${contentId}&title=${title}&userid=${userId}&type=${type}`;

    if (browser.isAndroid) {
      const url = `license=${encodeURIComponent(license)}&url=${encodeURIComponent(
        newActionUrl,
      )}&referer=${encodeURIComponent(
        referer,
      )}&debug=${debug}&version=${version}&pmp=${pmp}&android_referer_return=${refererReturn}&offline_check=${offlineCheck}&user_id=${userId}&from=none`;
      return `intent://?${url}#Intent;scheme=starplayerplus;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=kr.co.axissoft.starplayerplus;end`;
    }
    if (browser.isIos) {
      return `starplayerplus://?license=${encodeURIComponent(license)}&url=${encodeURIComponent(
        newActionUrl,
      )}&referer=${encodeURIComponent(
        referer,
      )}&debug=${debug}&version=${version}&pmp=${pmp}&referer_return=${refererReturn}&offline_check=${offlineCheck}&user_id=${userId}&from=none`;
    }
    return '';
  },

  setup() {
    if (!this.isStarPlayer) {
      return;
    }

    const player = this;

    // player.debug.log(window.navigator.userAgent.toLowerCase());

    if (is.empty(player.config.starplayer.app.referer)) {
      player.config.starplayer.app.referer = window.location.href;
    }

    if (is.empty(player.config.starplayer.app.license)) {
      // show message
      starplayerApp.showMessage.call(this, '라이선스 정보가 없습니다.', false);
      player.debug.log('라이선스 정보가 없습니다.');
      return;
    }

    if (is.empty(player.config.starplayer.app.actionUrl)) {
      // show message
      starplayerApp.showMessage.call(this, 'action url 정보가 없습니다.', false);
      player.debug.log('action url 정보가 없습니다.');
      return;
    }

    this.media.hidden = true;

    const container = createElement('div', { class: 'starplayer__app' });
    this.elements.starplayerapp = container;
    this.elements.wrapper.appendChild(container);

    if (player.poster) {
      starplayerApp.setPoster.call(player, player.poster, false);
    }

    const sources = starplayerApp.getSources.call(this);
    sources.forEach((source) => {
      const quality = source.getAttribute('size');
      const src = source.getAttribute('src');
      container.appendChild(starplayerApp.makeLink.call(this, `${quality}p`, src));
      // player.debug.log(quality, src);
    });

    if (browser.isIos) {
      container.appendChild(starplayerApp.makeInstallLink.call(this));
    }

    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }
  },

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    if (this.elements.message) {
      this.elements.message.removeAttribute('hidden');
      this.elements.text.innerHTML = message;
      toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
    }
  },

  getSources() {
    if (!this.isStarPlayer) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('sourcex'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');
      if (is.empty(type)) {
        return true;
      }
      return support.mime.call(this, type);
    });
  },

  destroy() {
    if (this.elements.starplayerapp) {
      this.elements.starplayerapp.remove();
    }
    this.media.hidden = false;
  },

  makeLink(title, url) {
    const ptag = createElement('p');
    const defaultAttributes = { class: 'starplayer__app__item', target: '_blank' };
    const atag = createElement('a', defaultAttributes, title);
    atag.href = starplayerApp.makeUrl.call(this, url);
    ptag.appendChild(atag);
    return ptag;
  },

  makeInstallLink() {
    const msg = i18n.get('installAppStore', this.config.starplayer);
    // const ptag = createElement('p');
    const defaultAttributes = { class: 'starplayer__app__appstore', target: '_blank' };
    const atag = createElement('a', defaultAttributes, msg);
    atag.href = this.config.urls.appStore.url;
    // ptag.appendChild(atag);
    return atag;
  },

  // // Toggle poster
  // togglePoster(enable) {
  //   toggleClass(this.elements.container, this.config.classNames.posterEnabled, enable);
  // },

  // Set the poster image (async)
  // Used internally for the poster setter, with the passive option forced to false
  setPoster(poster, passive = true) {
    // Don't override if call is passive
    if (passive && this.poster) {
      return Promise.reject(new Error('Poster already set'));
    }

    // Set property synchronously to respect the call order
    this.media.setAttribute('data-poster', poster);

    // Wait until ui is ready
    return loadImage
      .call(this, poster)
      .catch((err) => {
        // Hide poster on error unless it's been set by another call
        if (poster === this.poster) {
        }
        // Rethrow
        throw err;
      })
      .then(() => {
        // Prevent race conditions
        if (poster !== this.poster) {
          throw new Error('setPoster cancelled by later call to setPoster');
        }
      })
      .then(() => {
        Object.assign(this.elements.starplayerapp.style, {
          backgroundImage: `url('${poster}')`,
          // Reset backgroundSize as well (since it can be set to "cover" for padded thumbnails for youtube)
          backgroundSize: '',
        });
        return poster;
      });
  },
};

export default starplayerApp;
